import { ptsToPxls } from './pageLayoutMath';

/**
 * Reducer for boundary mouse-down event.
 *
 * Decides whether a drag should begin. If the click is a right-click or
 * targets the page-boundary (non-draggable area) it is a no-op.
 * Otherwise it ensures the break has an id, registers it in manualPageBreaks
 * if new, and returns the state patch.
 *
 * NOTE: mutates `breakObj` in place (assigns id + manual flag) to preserve
 * the original contract.
 *
 * @param {{ manualPageBreaks: Array }} state
 * @param {object} breakObj  - the boundary object (the `id` param from class)
 * @param {string} elClass   - className of the event target element
 * @param {number} button    - MouseEvent.button value
 * @returns {{ mouseDown: object, manualPageBreaks: Array } | null}
 */
export function mouseDownReducer(state, breakObj, elClass, button) {
	if (button === 2 || (elClass && elClass.includes('page-boundary'))) {
		return null;
	}

	const manualPageBreaks = state.manualPageBreaks;

	if (!breakObj.id) {
		breakObj.id = Math.ceil(Math.random() * 100000);
		breakObj.pageBreakId = breakObj.id;
		breakObj.manual = true;
		manualPageBreaks.push(breakObj);
	}

	return {
		mouseDown: {
			startCursor: 1,
			id: breakObj.id,
		},
		manualPageBreaks,
	};
}

/**
 * Reducer for mouse-move (throttled).
 *
 * Updates the dragged break's position. No-op if no drag is in progress.
 *
 * NOTE: mutates `mouseDown.pos` and the matching break's `pos` to preserve
 * the original contract.
 *
 * @param {{ mouseDown: object | null, manualPageBreaks: Array }} state
 * @param {number} refPts - cursor position in points relative to editor top
 * @returns {{ mouseDown: object, manualPageBreaks: Array } | null}
 */
export function mouseMoveReducer(state, refPts) {
	if (!state.mouseDown) {
		return null;
	}

	const mouseDown = state.mouseDown;
	mouseDown.pos = ptsToPxls(refPts);

	const manualPageBreaks = state.manualPageBreaks;
	const editing = manualPageBreaks.find(el => el.id === mouseDown.id);
	editing.pos = refPts;

	return { mouseDown, manualPageBreaks };
}

/**
 * Reducer for mouse-up (drop).
 *
 * Finds the break being dragged, checks whether it was dropped on top of
 * another break (overlap < 10pt). Returns enough information for the class
 * to perform the necessary side effects (entity removal, snap, addPageBreak).
 *
 * @param {{ mouseDown: object | null, manualPageBreaks: Array, pageBreaks: Array }} state
 * @returns {{
 *   theBreak: object,
 *   onTopOfAnother: object | undefined,
 *   manualPageBreaks: Array,
 * } | null}
 */
export function mouseUpReducer(state) {
	if (!state.mouseDown) {
		return null;
	}

	const mouseDown = state.mouseDown;
	const manualPageBreaks = state.manualPageBreaks;
	const theBreak = manualPageBreaks.find(item => mouseDown.id === item.id);

	if (!theBreak) {
		return null;
	}

	const mouseDownPos = theBreak.pos;
	const allBoundaries = (state.pageBreaks || []).concat(manualPageBreaks);
	const onTopOfAnother = allBoundaries.find(
		other => other.id !== mouseDown.id && Math.abs(other.pos - mouseDownPos) < 10
	);

	if (onTopOfAnother) {
		// remove the dragged break from manualPageBreaks
		const index = manualPageBreaks.findIndex(m => m.id === mouseDown.id);
		manualPageBreaks.splice(index, 1);
	}

	return { theBreak, onTopOfAnother, manualPageBreaks };
}
